import { useMemo } from "react";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import JsonLd from "../seo/JsonLd";
import { formatRate } from "../../utils/formatRate";
import { pricingTheme, type PricingTone } from "./pricingTheme";
import { PricingSectionHeader, formatCredits } from "./PricingSectionParts";
import { UNIT_SUFFIX, type PublicRateCard, type PublicRateLine } from "./rateCardTypes";

interface Props {
    tone: PricingTone;
    card: PublicRateCard;
    /** Publishes the price as schema.org JSON-LD. Only the public page should. */
    structuredData?: boolean;
}

/** Counts follow `smsBillingService.countSegments`: GSM-7 is 160, or 153 per part; UCS-2 is 70, or 67. */
const SEGMENT_EXAMPLES: Array<{ message: string; segments: number }> = [
    { message: "“Hi Priya, your appointment is confirmed for 3:00 PM tomorrow.” (61 characters)", segments: 1 },
    { message: "160 characters of plain text — the most one segment holds", segments: 1 },
    { message: "The same 160 characters with one emoji added", segments: 3 },
    { message: "A 300-character follow-up with a booking link", segments: 2 },
    { message: "A 100-character reminder in Hindi or Tamil", segments: 2 },
];

interface PriceSpan {
    low: number;
    high: number;
    currency: string;
}

/** Providers are not named on the page, so per-provider rates fold into one figure, or a range when HQ prices them apart. */
function spanOf(lines: PublicRateLine[]): PriceSpan {
    const amounts = lines.map((l) => l.amountMinor);
    return { low: Math.min(...amounts), high: Math.max(...amounts), currency: lines[0].currency };
}

function formatSpan({ low, high, currency }: PriceSpan, quantity = 1): string {
    const from = formatRate(low * quantity, currency);
    return low === high ? from : `${from}–${formatRate(high * quantity, currency)}`;
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export default function SmsRates({ tone, card, structuredData = false }: Props) {
    const t = pricingTheme(tone);
    const platform = card.sms.filter((l) => l.account !== "own");
    if (platform.length === 0) return null;

    const own = card.sms.find((l) => l.account === "own") ?? null;
    const isInr = card.currency === "inr";
    const price = spanOf(platform);
    const perSegment = formatSpan(price);
    const ownAccount = own?.carrierLabel ? lowerFirst(own.carrierLabel) : "your own SMS provider account";
    const credits = card.credits?.find((b) => b.chargeType === "SMS") ?? null;
    const threshold = card.billing.smsAutoChargeAtMinor;

    const facts: Array<{ question: string; answer: string }> = [
        {
            question: "What isn't charged?",
            answer: "Texts a provider rejects, and replies customers send to your number. An automated reply your agent sends back is a new message and is billed like any other.",
        },
        {
            question: "How is SMS usage collected?",
            answer: isInr
                ? "It is added to your Razorpay renewal or your monthly invoice, alongside your other usage."
                : `SMS runs on a balance of its own, charged to your saved card ${
                      threshold ? `each time it reaches ${formatRate(threshold, card.currency)}` : "when it reaches its threshold"
                  }. Anything left is collected at month end.`,
        },
        ...(credits
            ? [
                  {
                      question: "Do prepaid credits cover SMS?",
                      answer: `Yes. Each segment uses ${formatCredits(credits.credits)} ${
                          credits.credits === 1 ? "credit" : "credits"
                      }, and credits are used before anything is charged.`,
                  },
              ]
            : []),
        ...(isInr
            ? [
                  {
                      question: "What does DLT registration mean for me?",
                      answer: "TRAI requires every business SMS to an Indian number to carry a registered sender ID and an approved message template. FloGPT sends +91 messages only through DLT-registered Indian gateways, and a message without a template is stopped before it is sent, at no charge.",
                  },
              ]
            : [
                  {
                      question: "Texting the US or Canada?",
                      answer: "Carriers require business texts to +1 numbers to come from a number registered for A2P 10DLC, so register yours before texting North American customers.",
                  },
                  {
                      question: "Texting Indian numbers?",
                      answer: "Messages to +91 numbers always go through a DLT-registered Indian gateway, as TRAI requires, and need an approved message template.",
                  },
              ]),
    ];

    return (
        <section aria-labelledby="pricing-sms" className={t.section}>
            <PricingSectionHeader
                tone={tone}
                icon={ChatBubbleLeftRightIcon}
                id="pricing-sms"
                title="SMS"
                subtitle={
                    isInr
                        ? `Confirmations, reminders and follow-ups your agents text to Indian mobile numbers cost ${perSegment} per message segment.`
                        : `Confirmations, reminders and follow-ups your agents text customers cost ${perSegment} per message segment, at one rate for every destination outside India.`
                }
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className={t.card}>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${t.muted}`}>{isInr ? "India (+91)" : "Global"}</p>
                    <p className="mt-2 whitespace-nowrap">
                        <span className={`text-3xl font-extrabold tabular-nums ${t.heading}`}>{perSegment}</span>
                        <span className={`ml-1 text-base ${t.muted}`}>{UNIT_SUFFIX.segment}</span>
                    </p>
                    <p className={`mt-3 text-sm ${t.body}`}>
                        {isInr
                            ? "Sent on FloGPT's account through a DLT-registered Indian SMS gateway."
                            : "Sent from the number your AI agent calls from, so customers see one number for calls and texts."}
                    </p>
                </div>
                {own && (
                    <div className={t.card}>
                        <p className={`text-xs font-semibold uppercase tracking-wide ${t.muted}`}>Your own provider account</p>
                        <p className="mt-2 whitespace-nowrap">
                            {own.amountMinor > 0 ? (
                                <>
                                    <span className={`text-3xl font-extrabold tabular-nums ${t.heading}`}>{formatRate(own.amountMinor, own.currency)}</span>
                                    <span className={`ml-1 text-base ${t.muted}`}>{UNIT_SUFFIX.segment}</span>
                                </>
                            ) : (
                                <span className={`text-3xl font-extrabold ${t.heading}`}>No FloGPT fee</span>
                            )}
                        </p>
                        <p className={`mt-3 text-sm ${t.body}`}>
                            Connect {ownAccount} to send under your own DLT entity, sender IDs and templates. The provider bills you for each message
                            directly.
                        </p>
                    </div>
                )}
            </div>

            <h4 className={`mt-8 text-base font-semibold ${t.subheading}`}>What counts as an SMS segment?</h4>
            <p className={`mt-1 text-sm ${t.body}`}>
                One segment holds up to 160 standard characters — letters, digits and common punctuation. An emoji or a non-Latin script such as Hindi
                switches the whole message to Unicode, which holds 70. Longer messages are split into parts of 153 standard or 67 Unicode characters,
                and each part is billed as a segment.
            </p>
            <div className={`mt-4 overflow-x-auto rounded-lg border ${t.divider}`}>
                <table className={`min-w-full divide-y ${t.divider}`}>
                    <caption className="sr-only">Example SMS messages, their segment count and cost</caption>
                    <thead>
                        <tr className={`text-xs uppercase tracking-wide ${t.tableHead}`}>
                            <th scope="col" className="px-4 py-3 text-left font-semibold">
                                Message
                            </th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold">
                                Segments
                            </th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold">
                                Cost
                            </th>
                        </tr>
                    </thead>
                    <tbody className={`divide-y ${t.divider}`}>
                        {SEGMENT_EXAMPLES.map(({ message, segments }) => (
                            <tr key={message}>
                                <th scope="row" className={`px-4 py-3 text-left text-sm font-normal ${t.body}`}>
                                    {message}
                                </th>
                                <td className={`px-4 py-3 text-right text-sm tabular-nums ${t.heading}`}>{segments}</td>
                                <td className={`whitespace-nowrap px-4 py-3 text-right text-sm font-bold tabular-nums ${t.heading}`}>
                                    {formatSpan(price, segments)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <dl className="mt-8 grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
                {facts.map(({ question, answer }) => (
                    <div key={question}>
                        <dt className={`text-sm font-semibold ${t.subheading}`}>{question}</dt>
                        <dd className={`mt-1 text-sm ${t.body}`}>{answer}</dd>
                    </div>
                ))}
            </dl>

            {structuredData && <SmsStructuredData price={price} own={own} isInr={isInr} />}
        </section>
    );
}

const major = (minor: number) => (minor / 100).toFixed(2);

function unitPrice(span: PriceSpan) {
    return {
        "@type": "UnitPriceSpecification",
        priceCurrency: span.currency.toUpperCase(),
        ...(span.low === span.high ? { price: major(span.low) } : { minPrice: major(span.low), maxPrice: major(span.high) }),
        unitText: "SMS segment",
        referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "SMS segment" },
    };
}

function SmsStructuredData({ price, own, isInr }: { price: PriceSpan; own: PublicRateLine | null; isInr: boolean }) {
    const { low, high, currency } = price;
    const ownMinor = own?.amountMinor ?? null;
    const ownCurrency = own?.currency ?? currency;

    const data = useMemo(() => {
        const offers: Record<string, unknown>[] = [
            {
                "@type": "Offer",
                name: isInr ? "SMS segment to an Indian mobile number" : "SMS segment",
                priceSpecification: unitPrice({ low, high, currency }),
            },
        ];
        if (ownMinor !== null) {
            offers.push({
                "@type": "Offer",
                name: "SMS segment through your own provider account",
                priceSpecification: unitPrice({ low: ownMinor, high: ownMinor, currency: ownCurrency }),
            });
        }
        return {
            "@context": "https://schema.org",
            "@type": "Service",
            name: isInr ? "FloGPT SMS for Indian numbers" : "FloGPT SMS",
            serviceType: "Business SMS messaging",
            description: isInr
                ? "Appointment confirmations, reminders and follow-ups sent by AI agents to Indian mobile numbers through DLT-registered gateways, billed per message segment."
                : "Appointment confirmations, reminders and follow-ups sent by AI agents, billed per message segment at one rate for every destination outside India.",
            provider: { "@type": "Organization", name: "FloGPT" },
            ...(isInr ? { areaServed: { "@type": "Country", name: "India" } } : {}),
            offers,
        };
    }, [low, high, currency, ownMinor, ownCurrency, isInr]);

    return <JsonLd id="pricing-sms" data={data} />;
}
